import {
  recordImpressionByName,
  sendEvent,
} from '@JohnLewisPartnership/experiments-utils';
import { useCallback, useEffect, useRef } from 'react';
import { dispatchNewEvent } from '../../../utils/analytics/dispatchNewEvent';
import {
  isMin1280pxWide,
  isMin768pxWide,
} from '../../../utils/device/identifyDevice';

type UseSearchFocusParams = {
  hasStickyHeader: boolean;
  isFocused: boolean;
  setIsFocused: (isFocused: boolean) => void;
  monetate?: Partial<EnvVars['monetate']>;
  monetateExperiences: MonetateExperience[];
  monetateId?: string;
  showNav: boolean;
  stickyHeaderActive: boolean;
  typeaheadOpenClassName: string;
  updateTypeaheadState: (isOpen: boolean) => void;
  onDesktopBlur?: () => void;
};

type UseSearchFocusResult = {
  searchInputFocus: () => void;
  searchInputBlur: () => void;
  onSearchTabOut: (e?: React.FocusEvent<HTMLElement>) => void;
};

type ImpressionMonetateExperiences = Parameters<
  typeof recordImpressionByName
>[1];

export const useSearchFocus = ({
  hasStickyHeader,
  isFocused,
  setIsFocused,
  monetate,
  monetateExperiences,
  monetateId,
  showNav,
  stickyHeaderActive,
  typeaheadOpenClassName,
  updateTypeaheadState,
  onDesktopBlur,
}: UseSearchFocusParams): UseSearchFocusResult => {
  const scrollbarWidthRef = useRef(0);
  const searchInputBlurRef = useRef<() => void>(() => {});
  const hasDispatchedFocusEventRef = useRef(false);
  const impressionExperiences =
    monetateExperiences as unknown as ImpressionMonetateExperiences;

  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (event.keyCode === 27) {
      (document.activeElement as HTMLElement)?.blur();
      searchInputBlurRef.current();
    }
  }, []);

  const searchInputBlur = useCallback(() => {
    setIsFocused(false);
    hasDispatchedFocusEventRef.current = false;
    window.removeEventListener('keydown', handleEscKey);

    if (isMin768pxWide()) {
      onDesktopBlur?.();
    }

    // Old styling - only apply to desktop
    if (isMin1280pxWide()) {
      document.body.style.paddingRight = '';
      document.body.classList.remove(typeaheadOpenClassName);
    } else {
      window.scrollTo(0, 0);
    }
  }, [handleEscKey, onDesktopBlur, setIsFocused, typeaheadOpenClassName]);

  useEffect(
    function syncSearchInputBlurRef() {
      searchInputBlurRef.current = searchInputBlur;
    },
    [searchInputBlur]
  );

  useEffect(function setScrollbarWidth() {
    scrollbarWidthRef.current = window.innerWidth - document.body.clientWidth;
  }, []);

  useEffect(
    function cleanupEscListenerOnUnmount() {
      return function removeEscListener() {
        window.removeEventListener('keydown', handleEscKey);
      };
    },
    [handleEscKey]
  );

  const onSearchTabOut = useCallback(
    (e?: React.FocusEvent<HTMLElement>) => {
      if (!isFocused) {
        return;
      }

      if (
        (e?.relatedTarget as HTMLElement)?.id?.includes('desktop-menu-item')
      ) {
        searchInputBlur();
      }
    },
    [isFocused, searchInputBlur]
  );

  const searchInputFocus = useCallback(() => {
    window.scrollTo(0, 0);
    setIsFocused(true);
    window.addEventListener('keydown', handleEscKey);

    // Old styling - only apply to desktop
    if (isMin1280pxWide()) {
      document.body.classList.add(typeaheadOpenClassName);
      document.body.style.paddingRight = `${scrollbarWidthRef.current}px`;
    }

    if (isMin768pxWide() && hasStickyHeader && stickyHeaderActive) {
      sendEvent('sticky-search-header.search-focussed');
    }

    recordImpressionByName(
      monetate?.channel,
      impressionExperiences,
      'typeahead-content',
      monetateId
    );

    recordImpressionByName(
      monetate?.channel,
      impressionExperiences,
      'typeahead-recs',
      monetateId
    );

    recordImpressionByName(
      monetate?.channel,
      impressionExperiences,
      'typeahead-white',
      monetateId
    );

    if (!hasDispatchedFocusEventRef.current) {
      dispatchNewEvent('dataStateChange', {
        action: 'global.searchInputBoxFocus',
      });
      hasDispatchedFocusEventRef.current = true;
    }

    if (showNav) {
      updateTypeaheadState(true);
    }
  }, [
    handleEscKey,
    hasStickyHeader,
    monetate?.channel,
    monetateExperiences,
    monetateId,
    showNav,
    stickyHeaderActive,
    typeaheadOpenClassName,
    updateTypeaheadState,
  ]);

  return {
    searchInputFocus,
    searchInputBlur,
    onSearchTabOut,
  };
};
